// MIDI for the Rhythm Graph: reading Standard MIDI Files (uploads and the Groove MIDI Dataset),
// and writing a TrueChops score as one, for MIDI files people download and the DAW exports.
import { playbackOrder, soundingMeasure, DYNAMIC_LEVELS } from "../score-structure";
import { noteQuarterDuration } from "../book-practice-score";
import { midi as pianoMidi } from "../piano";
import { baseOf, isPitched, midiOf } from "../instruments";

export const PPQ = 480;
export const DRUM_CHANNEL = 9;
// General MIDI drum notes for each TrueChops instrument's staff positions (see INSTRUMENT_NOTE_TO_VOICE_MAP).
export const GM_DRUMS = {
  drumset: { F4: 36, C5: 38, E5: 42, D4: 44, F5: 51, D5: 50, B4: 47, A4: 43, G4: 41 },
  snare: { C5: 38, D5: 37, E5: 37, F5: 40, G5: 38 },
  tenors: { G5: 50, E5: 48, C5: 47, A4: 45, F4: 43 },
  bass: { G5: 45, E5: 43, C5: 41, A4: 36, F4: 35 },
  cymbals: { E5: 49, C5: 57 },
};
const GRACE_SECONDS = 0.0175;

// Each pitched sound gets a channel (never the drums'), for its General MIDI program. A score with more
// sounds than channels shares them.
function channelsOf(score) {
  const programs = [...new Set((score?.measures?.[0]?.parts || []).filter((part) => isPitched(part.instrument)).map((part) => midiOf(part.instrument).program))];
  const channelAt = (index) => { const channel = index % 15; return channel >= DRUM_CHANNEL ? channel + 1 : channel; };
  return { programs: programs.map((program, index) => [channelAt(index), program]), of: (instrument) => channelAt(Math.max(0, programs.indexOf(midiOf(instrument).program))) };
}

// Every note a score sounds, in playback order (repeats and jumps taken), the way TrueChops
// plays it: accents and dynamics set loudness, diddles add a second stroke and flams a grace
// note. Times are in quarter notes from the start; velocity is 0-1. `drums` says which note each staff
// position plays, when it isn't General MIDI's.
export function scoreEvents(score, tempo = 90, { drums = null } = {}) {
  const events = [], loudness = new Map(), tied = new Map(), measures = score?.measures || [], channels = channelsOf(score);
  let start = 0;
  for (const index of playbackOrder(measures)) {
    const written = measures[index], measure = measures[soundingMeasure(measures, index)];
    for (const part of measure.parts || []) {
      if (score.parts?.[part.instrument]?.enabled === false) continue;
      const piano = isPitched(part.instrument), map = drums || GM_DRUMS[baseOf(part.instrument)] || midiOf(part.instrument).drums;
      part.voices.forEach((voice, voiceIndex) => {
        const key = `${part.instrument}:${voiceIndex}`;
        let position = 0;
        voice.notes.forEach((note, noteIndex) => {
          const length = noteQuarterDuration(note, noteIndex, voice.tuplets || []);
          let level = loudness.get(key) ?? null;
          if (DYNAMIC_LEVELS[note.dynamic]) { level = DYNAMIC_LEVELS[note.dynamic]; loudness.set(key, level); }
          const ornaments = note.ornaments || "", accented = ornaments.includes("a");
          let velocity = level == null ? (accented ? 1 : piano ? 0.7 : 0.5) : accented ? Math.min(1, level + 0.3) : level;
          if (note.dynamic === "sfz" || note.dynamic === "rfz") velocity = Math.max(0.9, (level ?? DYNAMIC_LEVELS.mf) + 0.4);
          // A tied note carries on the one before it.
          const held = tied.get(key);
          tied.delete(key);
          if (held && note.notes?.length) {
            for (const event of held) event.length += length;
            if (note.tie) tied.set(key, held);
            position += length;
            return;
          }
          const sounded = [];
          for (const pitch of note.notes || []) {
            const number = piano ? pianoMidi(pitch) : map?.[pitch];
            if (!Number.isFinite(number)) continue;
            const event = { at: start + position, pitch: number, velocity: Math.min(1, velocity), channel: piano ? channels.of(part.instrument) : DRUM_CHANNEL, length: piano ? length : 0 };
            events.push(event);
            sounded.push(event);
            if (/[dc]/.test(ornaments)) events.push({ ...event, at: event.at + length / 2 });
            if (/[fc]/.test(ornaments)) events.push({ ...event, at: Math.max(0, event.at - GRACE_SECONDS * tempo / 60), velocity: 0.1 });
          }
          if (note.tie && piano && sounded.length) tied.set(key, sounded);
          position += length;
        });
      });
    }
    start += written.timeSig.num * 4 / written.timeSig.type;
  }
  return events.sort((a, b) => a.at - b.at || a.pitch - b.pitch);
}

const vlq = (value) => {
  const bytes = [value & 0x7f];
  while ((value >>= 7) > 0) bytes.unshift((value & 0x7f) | 0x80);
  return bytes;
};

// A score as a format-0 MIDI file: tempo, time signatures, and every note (drums on channel 10).
export function scoreToMidi(score, tempo = 90) {
  const timed = [];
  const tick = (quarters) => Math.round(quarters * PPQ);
  timed.push({ tick: 0, order: 0, bytes: [0xff, 0x51, 0x03, ...[16, 8, 0].map((shift) => (Math.round(60e6 / tempo) >> shift) & 0xff)] });
  let at = 0, previous = "";
  for (const index of playbackOrder(score.measures)) {
    const { num, type } = score.measures[index].timeSig, signature = `${num}/${type}`;
    if (signature !== previous) timed.push({ tick: tick(at), order: 1, bytes: [0xff, 0x58, 0x04, num, Math.log2(type), 24, 8] });
    previous = signature;
    at += num * 4 / type;
  }
  // Which General MIDI sound each channel plays (a channel starts out as a piano).
  for (const [channel, program] of channelsOf(score).programs) if (program) timed.push({ tick: 0, order: 1, bytes: [0xc0 | channel, program] });
  for (const event of scoreEvents(score, tempo)) {
    const velocity = Math.max(1, Math.min(127, Math.round(event.velocity * 127)));
    const on = tick(event.at), off = on + Math.max(1, event.length ? tick(event.length) - 1 : PPQ / 8);
    timed.push({ tick: on, order: 3, bytes: [0x90 | event.channel, event.pitch, velocity] });
    timed.push({ tick: off, order: 2, bytes: [0x80 | event.channel, event.pitch, 0] });
  }
  timed.sort((a, b) => a.tick - b.tick || a.order - b.order);
  const track = [];
  let last = 0;
  for (const { tick: when, bytes } of timed) { track.push(...vlq(when - last), ...bytes); last = when; }
  track.push(0, 0xff, 0x2f, 0x00);
  const header = [0x4d, 0x54, 0x68, 0x64, 0, 0, 0, 6, 0, 0, 0, 1, (PPQ >> 8) & 0xff, PPQ & 0xff];
  const length = track.length;
  return new Uint8Array([...header, 0x4d, 0x54, 0x72, 0x6b, (length >>> 24) & 0xff, (length >>> 16) & 0xff, (length >>> 8) & 0xff, length & 0xff, ...track]);
}

// Reads a Standard MIDI File: its resolution, tempo, time- and key-signature changes, track
// names, and every note struck, with its track and its time and length in quarter notes.
// Throws a readable error for anything else.
export function parseMidi(input) {
  const data = input instanceof Uint8Array ? input : new Uint8Array(input);
  let at = 0;
  const fail = () => { throw new Error("That isn't a readable MIDI file."); };
  const u32 = () => { if (at + 4 > data.length) fail(); const value = ((data[at] << 24) >>> 0) + (data[at + 1] << 16) + (data[at + 2] << 8) + data[at + 3]; at += 4; return value; };
  const u16 = () => { if (at + 2 > data.length) fail(); const value = (data[at] << 8) + data[at + 1]; at += 2; return value; };
  const text = (length) => String.fromCharCode(...data.subarray(at, at + length));
  if (text(4) !== "MThd") fail();
  at += 4;
  const headerLength = u32(), format = u16(), trackCount = u16(), division = u16();
  at = 8 + headerLength;
  if (division & 0x8000) throw new Error("MIDI files timed in SMPTE frames aren't supported.");
  if (!division || trackCount > 64) fail();
  const tempos = [], timeSignatures = [], keySignatures = [], trackNames = [], notes = [];
  for (let track = 0; track < trackCount && at < data.length; track++) {
    if (text(4) !== "MTrk") fail();
    at += 4;
    const size = u32(), end = at + size;
    if (end > data.length) fail();
    // Notes still sounding, by channel and key, in the order they were struck.
    let tick = 0, status = 0;
    const sounding = new Map();
    const release = (channel, key) => {
      const open = sounding.get(channel * 128 + key);
      if (open?.length) open.shift().endTick = tick;
    };
    const read = () => { if (at >= end) fail(); return data[at++]; };
    const length = () => { let value = 0, byte; do { byte = read(); value = (value << 7) | (byte & 0x7f); } while (byte & 0x80); return value; };
    while (at < end) {
      tick += length();
      let byte = read();
      if (byte === 0xff) {
        const type = read(), size = length(), body = data.subarray(at, at + size);
        at += size;
        if (type === 0x51 && size === 3) tempos.push({ tick, bpm: 60e6 / ((body[0] << 16) | (body[1] << 8) | body[2]) });
        if (type === 0x58 && size >= 2) timeSignatures.push({ tick, num: body[0], den: 2 ** body[1] });
        // Sharps (positive) or flats (negative), and whether it's minor.
        if (type === 0x59 && size === 2) keySignatures.push({ tick, sharps: (body[0] << 24) >> 24, minor: body[1] === 1 });
        if (type === 0x03 && !trackNames[track]) trackNames[track] = String.fromCharCode(...body).trim();
        if (type === 0x2f) break;
        continue;
      }
      if (byte === 0xf0 || byte === 0xf7) { at += length(); continue; }
      if (byte & 0x80) status = byte; else { at--; if (!status) fail(); }
      byte = status;
      const kind = byte & 0xf0, channel = byte & 0x0f;
      const first = read(), second = kind === 0xc0 || kind === 0xd0 ? 0 : read();
      if (kind === 0x90 && second > 0) {
        release(channel, first);
        const note = { tick, channel, note: first, velocity: second, track };
        notes.push(note);
        const key = channel * 128 + first;
        if (!sounding.has(key)) sounding.set(key, []);
        sounding.get(key).push(note);
      } else if (kind === 0x80 || kind === 0x90) release(channel, first);
    }
    at = end;
  }
  if (notes.length > 200000) throw new Error("That MIDI file is too long.");
  notes.sort((a, b) => a.tick - b.tick || a.note - b.note);
  for (const note of notes) {
    note.quarter = note.tick / division;
    note.length = Math.max(0, ((note.endTick ?? note.tick) - note.tick) / division);
    delete note.endTick;
  }
  return { format, ppq: division, tempos: tempos.sort((a, b) => a.tick - b.tick), timeSignatures: timeSignatures.sort((a, b) => a.tick - b.tick),
    keySignatures: keySignatures.sort((a, b) => a.tick - b.tick), trackNames, notes };
}
